// src/Components/AdminCharts.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import './AdminCharts.css';

/* ==========================================================================
   Utilities
   ========================================================================== */

/** Animate a number from 0 → target when the element scrolls into view. */
export function useCountUp(target = 0, duration = 900) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const start = () => {
      if (started.current) return;
      started.current = true;
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - p, 3);
        setValue(Math.round(target * eased));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) start();
        });
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);

  return { value, ref };
}

/** Simple polyline path builder from an array of numbers. */
function buildPath(values, width, height, padding = 6) {
  if (!values.length) return '';
  const max = Math.max(...values, 1);
  const step = values.length > 1 ? (width - padding * 2) / (values.length - 1) : 0;
  return values
    .map((v, i) => {
      const x = padding + i * step;
      const y = height - padding - (v / max) * (height - padding * 2);
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

/* ==========================================================================
   1. AREA CHART — Contact inquiries over time
   ========================================================================== */

export function AreaChart({
  data = [],          // [{ label: 'Mon', value: 3 }, ...]
  height = 220,
  color = '#4f46e5',
  title = 'Activity',
  subtitle = '',
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const width = 720;
  const values = data.map((d) => d.value);
  const max = Math.max(...values, 1);
  const step = data.length > 1 ? (width - 40) / (data.length - 1) : 0;

  const points = data.map((d, i) => ({
    x: 20 + i * step,
    y: height - 30 - (d.value / max) * (height - 60),
    ...d,
  }));

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');

  const areaPath =
    points.length > 0
      ? `${linePath} L ${points[points.length - 1].x.toFixed(2)} ${height - 30} L ${points[0].x.toFixed(2)} ${height - 30} Z`
      : '';

  const gradId = useMemo(() => `areaGrad-${Math.random().toString(36).slice(2, 8)}`, []);

  return (
    <div className="chart-card">
      <div className="chart-card-head">
        <div>
          <h3 className="chart-card-title">{title}</h3>
          {subtitle && <span className="chart-card-sub">{subtitle}</span>}
        </div>
        <span className="chart-badge">Last 7 days</span>
      </div>

      <svg
        className="area-svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={title}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* horizontal grid lines */}
        {[0, 1, 2, 3].map((i) => {
          const y = 20 + i * ((height - 50) / 3);
          return (
            <line
              key={i}
              x1="20"
              x2={width - 20}
              y1={y}
              y2={y}
              className="grid-line"
            />
          );
        })}

        {/* area fill */}
        {mounted && (
          <path
            d={areaPath}
            fill={`url(#${gradId})`}
            className="area-fill"
          />
        )}

        {/* line */}
        {mounted && (
          <path
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="area-line"
            style={{
              strokeDasharray: 2000,
              strokeDashoffset: 2000,
            }}
          />
        )}

        {/* points */}
        {mounted &&
          points.map((p, i) => (
            <g key={i} className="area-point-group" style={{ animationDelay: `${i * 70 + 400}ms` }}>
              <circle cx={p.x} cy={p.y} r="9" fill={color} fillOpacity="0.12" />
              <circle cx={p.x} cy={p.y} r="3.5" fill="#fff" stroke={color} strokeWidth="2" />
              <text
                x={p.x}
                y={p.y - 12}
                textAnchor="middle"
                className="area-point-value"
              >
                {p.value}
              </text>
            </g>
          ))}

        {/* x labels */}
        {points.map((p, i) => (
          <text
            key={i}
            x={p.x}
            y={height - 8}
            textAnchor="middle"
            className="area-axis-label"
          >
            {p.label}
          </text>
        ))}
      </svg>
    </div>
  );
}

/* ==========================================================================
   2. DONUT CHART — Distribution
   ========================================================================== */

const DONUT_COLORS = [
  '#4f46e5', // indigo
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
];

export function DonutChart({
  data = [],          // [{ label: 'Users', value: 12 }, ...]
  size = 200,
  thickness = 22,
  title = 'Distribution',
  subtitle = '',
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 120);
    return () => clearTimeout(t);
  }, []);

  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const radius = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;

  let offsetAcc = 0;
  const segments = data.map((d, i) => {
    const fraction = d.value / total;
    const dash = fraction * circumference;
    const seg = {
      ...d,
      color: DONUT_COLORS[i % DONUT_COLORS.length],
      dash,
      gap: circumference - dash,
      offset: -offsetAcc,
      pct: Math.round(fraction * 100),
    };
    offsetAcc += dash;
    return seg;
  });

  return (
    <div className="chart-card">
      <div className="chart-card-head">
        <div>
          <h3 className="chart-card-title">{title}</h3>
          {subtitle && <span className="chart-card-sub">{subtitle}</span>}
        </div>
      </div>

      <div className="donut-wrap">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="donut-svg"
          role="img"
          aria-label={title}
        >
          {/* track */}
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke="#eef2f7"
            strokeWidth={thickness}
          />

          {/* segments */}
          {segments.map((s, i) => (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeLinecap="butt"
              strokeDasharray={`${s.dash} ${s.gap}`}
              strokeDashoffset={s.offset}
              transform={`rotate(-90 ${cx} ${cy})`}
              className="donut-segment"
              style={{
                opacity: mounted ? 1 : 0,
                transition: `stroke-dasharray 0.9s cubic-bezier(0.16,1,0.3,1) ${i * 100}ms, opacity 0.4s ease ${i * 100}ms`,
                strokeDasharray: mounted
                  ? `${s.dash} ${s.gap}`
                  : `0 ${circumference}`,
              }}
            />
          ))}

          {/* center label */}
          <text
            x={cx}
            y={cy - 4}
            textAnchor="middle"
            className="donut-center-value"
          >
            {total}
          </text>
          <text
            x={cx}
            y={cy + 16}
            textAnchor="middle"
            className="donut-center-label"
          >
            Total
          </text>
        </svg>

        <ul className="donut-legend">
          {segments.map((s, i) => (
            <li key={i} className="donut-legend-item" style={{ animationDelay: `${i * 60}ms` }}>
              <span
                className="donut-legend-dot"
                style={{ background: s.color }}
              />
              <span className="donut-legend-label">{s.label}</span>
              <span className="donut-legend-value">{s.value}</span>
              <span className="donut-legend-pct">{s.pct}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ==========================================================================
   3. BAR CHART — Module comparison
   ========================================================================== */

export function BarChart({
  data = [],          // [{ label: 'Users', value: 12, color?: '#4f46e5' }, ...]
  height = 220,
  title = 'Comparison',
  subtitle = '',
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const max = Math.max(...data.map((d) => d.value), 1);
  const barColors = DONUT_COLORS;

  return (
    <div className="chart-card">
      <div className="chart-card-head">
        <div>
          <h3 className="chart-card-title">{title}</h3>
          {subtitle && <span className="chart-card-sub">{subtitle}</span>}
        </div>
      </div>

      <div className="bar-chart" style={{ height }}>
        {data.map((d, i) => {
          const pct = (d.value / max) * 100;
          const color = d.color || barColors[i % barColors.length];
          return (
            <div key={i} className="bar-col">
              <span className="bar-value">{d.value}</span>
              <div className="bar-track">
                <div
                  className="bar-fill"
                  style={{
                    height: mounted ? `${pct}%` : '0%',
                    background: `linear-gradient(180deg, ${color}, ${color}cc)`,
                    transition: `height 0.9s cubic-bezier(0.16,1,0.3,1) ${i * 90}ms`,
                  }}
                />
              </div>
              <span className="bar-label">{d.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ==========================================================================
   4. SPARKLINE — Tiny inline chart for metric cards
   ========================================================================== */

export function Sparkline({
  values = [],
  width = 110,
  height = 34,
  color = '#4f46e5',
  fill = true,
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 150);
    return () => clearTimeout(t);
  }, []);

  if (!values.length) {
    values = [1, 2, 3, 2, 4, 3, 5];
  }

  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const step = (width - 8) / (values.length - 1 || 1);

  const points = values.map((v, i) => ({
    x: 4 + i * step,
    y: height - 4 - ((v - min) / range) * (height - 10),
  }));

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');

  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(2)} ${height} L ${points[0].x.toFixed(2)} ${height} Z`;

  const gradId = useMemo(
    () => `sparkGrad-${Math.random().toString(36).slice(2, 8)}`,
    []
  );

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="sparkline"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {fill && (
        <path
          d={areaPath}
          fill={`url(#${gradId})`}
          style={{
            opacity: mounted ? 1 : 0,
            transition: 'opacity 0.7s ease 0.3s',
          }}
        />
      )}

      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="sparkline-path"
      />

      {/* Last point highlight */}
      <circle
        cx={points[points.length - 1].x}
        cy={points[points.length - 1].y}
        r="3"
        fill={color}
        className="sparkline-dot"
      />
    </svg>
  );
}

/* ==========================================================================
   5. PROGRESS RING — Big animated % ring
   ========================================================================== */

export function ProgressRing({
  value = 0,
  max = 100,
  size = 140,
  thickness = 14,
  color = '#4f46e5',
  label = '',
  sublabel = '',
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 200);
    return () => clearTimeout(t);
  }, []);

  const pct = Math.min(1, Math.max(0, value / max));
  const radius = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = pct * circumference;

  return (
    <div className="ring-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="#eef2f7"
          strokeWidth={thickness}
        />
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={`${mounted ? dash : 0} ${circumference}`}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{
            transition: 'stroke-dasharray 1.1s cubic-bezier(0.16,1,0.3,1)',
          }}
        />
        <text
          x={cx}
          y={cy + 6}
          textAnchor="middle"
          className="ring-value"
        >
          {Math.round(pct * 100)}%
        </text>
      </svg>
      {label && <span className="ring-label">{label}</span>}
      {sublabel && <span className="ring-sublabel">{sublabel}</span>}
    </div>
  );
}